import { useLang } from "@/context/LangContext";
import { Clock, AlertTriangle, Flag } from "lucide-react";
import { hoursToDays } from "@/lib/hours";
import { jumpToExecPhase } from "@/lib/phaseNav";

const fmtN = (n) => Math.abs(n - Math.round(n)) < 0.05 ? String(Math.round(n)) : n.toFixed(1).replace(".", ",");

const complexityColors = { "Einfach": "#00F0FF", "Mittel": "#FFB800", "Komplex": "#FF3B30" };
const riskColors = { "Niedrig": "#00F0FF", "Low": "#00F0FF", "Faible": "#00F0FF", "Bajo": "#00F0FF", "Mittel": "#FFB800", "Medium": "#FFB800", "Moyen": "#FFB800", "Medio": "#FFB800", "Hoch": "#FF3B30", "High": "#FF3B30", "Eleve": "#FF3B30", "Alto": "#FF3B30" };

export function CostBreakdown({ analysis, hourlyRate }) {
  const { t } = useLang();
  const items = analysis?.cost_breakdown || [];
  if (!items.length) return null;
  const rate = parseFloat(hourlyRate) || 0;
  const totalMin = items.reduce((s, i) => s + (i.hours_min || 0), 0);
  const totalMax = items.reduce((s, i) => s + (i.hours_max || 0), 0);
  return (
    <div className="glass-surface rounded-xl p-5" data-testid="cost-breakdown">
      <div className="flex items-center gap-2 mb-1">
        <Clock className="w-4 h-4 text-[#00F0FF]" />
        <h3 className="font-['Outfit'] text-sm font-semibold text-white">{t.cost.title}</h3>
      </div>
      <p className="text-[10px] text-[#606070] mb-4 ml-6">{t.cost.sub}</p>
      <div className="space-y-1.5">
        {items.map((item, i) => {
          const avg = ((item.hours_min || 0) + (item.hours_max || 0)) / 2;
          const pct = totalMax > 0 ? ((item.hours_max || 0) / totalMax) * 100 : 0;
          return (
            <div key={i} className="group">
              <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/[0.02] border border-white/5 hover:border-white/10 transition-colors">
                <span className="w-5 text-[10px] font-['JetBrains_Mono'] text-[#606070]">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-white font-medium truncate">{item.component}</span>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-[10px] font-['JetBrains_Mono']" style={{ color: complexityColors[item.complexity] || "#A0A0B0" }}>{item.complexity}</span>
                      <span className="text-xs font-['JetBrains_Mono'] text-white" title={`≈ ${fmtN(hoursToDays(item.hours_min))}-${fmtN(hoursToDays(item.hours_max))} Tage`}>
                        {item.hours_min}-{item.hours_max}h
                        <span className="text-[#606070] ml-1">({fmtN(hoursToDays(item.hours_min))}-{fmtN(hoursToDays(item.hours_max))} T.)</span>
                      </span>
                      {rate > 0 && <span className="text-xs font-['JetBrains_Mono'] text-[#00F0FF]">{(avg * rate).toLocaleString("de-DE", { maximumFractionDigits: 0 })} EUR</span>}
                    </div>
                  </div>
                  <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: complexityColors[item.complexity] || "#A0A0B0", opacity: 0.6 }} />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {/* Total */}
        <div className="flex items-center justify-between px-3 py-2 mt-2 border-t border-white/10">
          <span className="text-xs font-['Outfit'] font-semibold text-white">{t.cost.total}</span>
          <div className="flex items-center gap-3">
            <span className="text-sm font-['JetBrains_Mono'] font-bold text-white">
              {totalMin}-{totalMax}h
              <span className="text-[#A0A0B0] ml-1 text-xs font-normal">≈ {fmtN(hoursToDays(totalMin))}-{fmtN(hoursToDays(totalMax))} Tage</span>
            </span>
            {rate > 0 && <span className="text-sm font-['JetBrains_Mono'] font-bold neon-text">{(((totalMin + totalMax) / 2) * rate).toLocaleString("de-DE", { maximumFractionDigits: 0 })} EUR</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

export function RiskPanel({ analysis }) {
  const { t } = useLang();
  const risks = analysis?.risks || [];
  if (!risks.length) return null;
  return (
    <div className="glass-surface rounded-xl p-5" data-testid="risk-panel">
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle className="w-4 h-4 text-[#FFB800]" />
        <h3 className="font-['Outfit'] text-sm font-semibold text-white">{t.risk.title}</h3>
      </div>
      <p className="text-[10px] text-[#606070] mb-4 ml-6">{t.risk.sub}</p>
      <div className="space-y-2">
        {risks.map((r, i) => {
          const color = riskColors[r.level] || "#A0A0B0";
          return (
            <div key={i} className="flex items-start gap-3 px-3 py-2.5 rounded-lg border transition-colors" style={{ background: `${color}05`, borderColor: `${color}15` }}>
              <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: color }} />
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs text-white font-medium">{r.component}</span>
                  <span className="text-[10px] font-['JetBrains_Mono'] px-1.5 py-0.5 rounded" style={{ color, background: `${color}15` }}>{r.level}</span>
                </div>
                <span className="text-[11px] text-[#808090] block">{r.description}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function MilestoneTimeline({ analysis }) {
  const { t } = useLang();
  const milestones = analysis?.milestones || [];
  if (!milestones.length) return null;
  const totalWeeks = milestones.reduce((s, m) => s + (m.weeks || 0), 0);
  return (
    <div className="glass-surface rounded-xl p-5" data-testid="milestone-timeline">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Flag className="w-4 h-4 text-[#00F0FF]" />
          <h3 className="font-['Outfit'] text-sm font-semibold text-white">{t.milestone.title}</h3>
        </div>
        <span className="text-xs font-['JetBrains_Mono'] text-[#A0A0B0]">{t.milestone.total_weeks}: <span className="text-white font-bold">{totalWeeks} {t.milestone.weeks}</span></span>
      </div>
      <p className="text-[10px] text-[#606070] mb-4 ml-6">{t.milestone.sub}</p>
      {/* Visual timeline bar */}
      <div className="flex gap-0.5 mb-4 rounded-full overflow-hidden h-3">
        {milestones.map((m, i) => {
          const pct = totalWeeks > 0 ? (m.weeks / totalWeeks) * 100 : 0;
          const colors = ["#00F0FF", "#33BBFF", "#FFB800", "#FF8800", "#FF3B30"];
          return <div key={i} className="h-full relative group" style={{ width: `${pct}%`, background: colors[i % colors.length], opacity: 0.7 }} title={`${m.name}: ${m.weeks} ${t.milestone.weeks}`} />;
        })}
      </div>
      <div className="space-y-3">
        {milestones.map((m, i) => {
          const colors = ["#00F0FF", "#33BBFF", "#FFB800", "#FF8800", "#FF3B30"];
          const col = colors[i % colors.length];
          return (
            <div
              key={i}
              id={`milestone-phase-${m.phase}`}
              data-testid={`milestone-phase-${m.phase}`}
              className="flex items-start gap-3 p-2 -mx-2 rounded-md transition-colors cursor-pointer hover:bg-white/[0.03]"
              onClick={() => jumpToExecPhase(m.phase)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  jumpToExecPhase(m.phase);
                }
              }}
              role="button"
              tabIndex={0}
              title="Springe zur Executive-Summary-Phase"
            >
              <div className="flex flex-col items-center flex-shrink-0">
                <div className="w-8 h-8 flex items-center justify-center rounded-full border-2 text-xs font-['JetBrains_Mono'] font-bold" style={{ borderColor: col, color: col }}>{m.phase}</div>
                {i < milestones.length - 1 && <div className="w-0.5 h-6 bg-white/10 mt-1" />}
              </div>
              <div className="pt-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm text-white font-medium font-['Outfit']">{m.name}</span>
                  <span className="text-[10px] font-['JetBrains_Mono'] px-2 py-0.5 rounded-full" style={{ color: col, background: `${col}15` }}>{m.weeks} {t.milestone.weeks}</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {(m.components || []).map((c, j) => (
                    <span key={j} className="text-[10px] px-1.5 py-0.5 bg-white/5 border border-white/10 text-[#A0A0B0] rounded font-['JetBrains_Mono']">{c}</span>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
